import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingCart, Package, Truck, CheckCircle, Clock,
    X, Eye, MapPin, Phone, Mail, User, AlertCircle,
    Search, Download, RefreshCw, Calendar, FileText,
    Printer, DollarSign, Copy, ExternalLink, Check,
    ArrowRight, Edit, BarChart2, Star, Upload, ImageIcon
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import OrderInvoice from '../components/OrderInvoice';

const VendorOrders = () => {
    const { token, user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showShippingModal, setShowShippingModal] = useState(false);
    const [uploadingDocs, setUploadingDocs] = useState({ delivery: false, bill: false, agreement: false });
    const [shippingForm, setShippingForm] = useState({
        delivery_person_name: '',
        delivery_person_phone: '',
        vehicle_type: '',
        vehicle_number: '',
        expected_delivery: '',
        delivery_notes: ''
    });

    const printRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Packing-Slip-${selectedOrder?.id || 'Order'}`,
    });

    const isInitialLoad = useRef(true);

    const fetchOrders = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await axios.get(`${API}/api/vendor/assigned-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(res.data);
        } catch (error) {
            console.log('Using demo data');
            setOrders([
                {
                    id: 'ORD-001',
                    status: 'assigned',
                    total_amount: 85000,
                    shipping_address: '123 Solar St, Green City, Delhi 110001',
                    billing_address: '123 Solar St, Green City, Delhi 110001',
                    created_at: '2024-12-27T10:30:00Z',
                    assigned_at: '2024-12-27T11:00:00Z',
                    items: [
                        { product_id: '1', name: 'Solar Panel 400W Mono PERC', price: 8500, quantity: 10, sku: 'SP-400-MONO' }
                    ],
                    customer: { name: 'Rahul Sharma', email: 'rahul@email.com', phone: '+91 98765 43210' },
                    timeline: [
                        { status: 'created', timestamp: '2024-12-27T10:30:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-27T11:00:00Z', note: 'Assigned to your store' }
                    ],
                    payment_status: 'paid',
                    notes: 'Customer prefers morning delivery'
                },
                {
                    id: 'ORD-002',
                    status: 'processing',
                    total_amount: 150000,
                    shipping_address: '456 Energy Ave, Andheri West, Mumbai 400053',
                    billing_address: '456 Energy Ave, Andheri West, Mumbai 400053',
                    created_at: '2024-12-26T14:00:00Z',
                    assigned_at: '2024-12-26T15:30:00Z',
                    items: [
                        { product_id: '3', name: '10kW Commercial Solar System', price: 150000, quantity: 1, sku: 'COM-10KW-SYS' }
                    ],
                    customer: { name: 'Priya Enterprises Pvt Ltd', email: 'procurement@priya.com', phone: '+91 22 4567 8901' },
                    timeline: [
                        { status: 'created', timestamp: '2024-12-26T14:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-26T15:30:00Z', note: 'Assigned to your store' },
                        { status: 'processing', timestamp: '2024-12-27T09:00:00Z', note: 'Started processing' }
                    ],
                    payment_status: 'paid',
                    notes: 'Commercial installation - requires site visit'
                },
                {
                    id: 'ORD-003',
                    status: 'shipped',
                    total_amount: 32000,
                    shipping_address: '789 Green Blvd, Koramangala, Bangalore 560095',
                    billing_address: '789 Green Blvd, Koramangala, Bangalore 560095',
                    created_at: '2024-12-25T09:00:00Z',
                    assigned_at: '2024-12-25T10:00:00Z',
                    items: [
                        { product_id: '4', name: 'Lithium Battery 10kWh Storage', price: 32000, quantity: 1, sku: 'BAT-10KWH-LI' }
                    ],
                    customer: { name: 'Vikram Singh', email: 'vikram@home.com', phone: '+91 98001 23456' },
                    tracking_number: 'BLUEDART123456789',
                    carrier: 'BlueDart',
                    expected_delivery: '2024-12-30T18:00:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-25T09:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-25T10:00:00Z', note: 'Assigned' },
                        { status: 'processing', timestamp: '2024-12-26T09:00:00Z', note: 'Preparing shipment' },
                        { status: 'shipped', timestamp: '2024-12-27T14:00:00Z', note: 'Shipped via BlueDart' }
                    ],
                    payment_status: 'paid'
                },
                {
                    id: 'ORD-004',
                    status: 'delivered',
                    total_amount: 18000,
                    shipping_address: '321 Power Lane, T Nagar, Chennai 600017',
                    billing_address: '321 Power Lane, T Nagar, Chennai 600017',
                    created_at: '2024-12-20T11:00:00Z',
                    assigned_at: '2024-12-20T12:00:00Z',
                    items: [
                        { product_id: '5', name: 'Hybrid Inverter 5kW', price: 18000, quantity: 1, sku: 'INV-5KW-HYB' }
                    ],
                    customer: { name: 'Ananya Reddy', email: 'ananya@email.com', phone: '+91 98765 11223' },
                    tracking_number: 'DELHIVERY987654321',
                    carrier: 'Delhivery',
                    delivered_at: '2024-12-25T10:30:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-20T11:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-20T12:00:00Z', note: 'Assigned' },
                        { status: 'processing', timestamp: '2024-12-21T09:00:00Z', note: 'Processing' },
                        { status: 'shipped', timestamp: '2024-12-23T11:00:00Z', note: 'Shipped' },
                        { status: 'delivered', timestamp: '2024-12-25T10:30:00Z', note: 'Delivered successfully' }
                    ],
                    payment_status: 'paid'
                },
                {
                    id: 'ORD-005',
                    status: 'completed',
                    total_amount: 45000,
                    shipping_address: '555 Eco Way, Baner, Pune 411045',
                    billing_address: '555 Eco Way, Baner, Pune 411045',
                    created_at: '2024-12-15T15:00:00Z',
                    assigned_at: '2024-12-15T16:00:00Z',
                    items: [
                        { product_id: '1', name: 'Solar Panel 400W Mono PERC', quantity: 15, price: 3000, sku: 'SP-400-MONO' }
                    ],
                    customer: { name: 'Meera Joshi', email: 'meera@home.com', phone: '+91 98765 99887' },
                    delivered_at: '2024-12-20T11:00:00Z',
                    installation_date: '2024-12-22T10:00:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-15T15:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-15T16:00:00Z', note: 'Assigned' },
                        { status: 'shipped', timestamp: '2024-12-17T09:00:00Z', note: 'Shipped' },
                        { status: 'delivered', timestamp: '2024-12-20T11:00:00Z', note: 'Delivered' },
                        { status: 'completed', timestamp: '2024-12-22T16:00:00Z', note: 'Installation completed' }
                    ],
                    payment_status: 'paid',
                    customer_rating: 5,
                    customer_review: 'Excellent service and quality products!'
                }
            ]);
        }
        if (!silent) setLoading(false);
    }, [token]);

    useEffect(() => {
        fetchOrders();
        isInitialLoad.current = false;
        const interval = setInterval(() => fetchOrders(true), 30000);
        return () => clearInterval(interval);
    }, [fetchOrders]);

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            await axios.put(`${API}/api/orders/${orderId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Order status updated to ${newStatus}`);
            fetchOrders();
        } catch (error) {
            setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder({ ...selectedOrder, status: newStatus });
            }
            toast.success(`Order status updated to ${newStatus}`);
        }
    };

    const handleShipOrder = async () => {
        if (!shippingForm.delivery_person_name || !shippingForm.delivery_person_phone || !shippingForm.vehicle_number) {
            toast.error('Please fill in delivery person name, phone, and vehicle number');
            return;
        }
        try {
            await axios.put(`${API}/api/orders/${selectedOrder.id}/ship`,
                shippingForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Shipping details added successfully');
            fetchOrders();
        } catch (error) {
            // Fallback: update locally
            setOrders(orders.map(o => o.id === selectedOrder.id
                ? { ...o, status: 'shipped', shipping_info: shippingForm }
                : o
            ));
            setSelectedOrder({ ...selectedOrder, status: 'shipped', shipping_info: shippingForm });
            toast.success('Shipping details added');
        }
        setShowShippingModal(false);
        setShippingForm({ delivery_person_name: '', delivery_person_phone: '', vehicle_type: '', vehicle_number: '', expected_delivery: '', delivery_notes: '' });
    };

    const handleDocUpload = async (e, docType) => {
        const file = e.target.files?.[0];
        if (!file || !selectedOrder) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            setUploadingDocs(prev => ({ ...prev, [docType]: true }));
            toast.loading('Uploading document...', { id: `upload-${docType}` });
            
            const uploadRes = await axios.post(`${API}/api/upload/image`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const imageUrl = uploadRes.data.url;
            
            const payload = {};
            if (docType === 'delivery') payload.pow_delivery_url = imageUrl;
            if (docType === 'bill') payload.pow_bill_url = imageUrl;
            if (docType === 'agreement') payload.pow_agreement_url = imageUrl;

            await axios.put(`${API}/api/orders/${selectedOrder.id}/proof-of-work`, 
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const updatedOrder = { ...selectedOrder, ...payload, proof_of_work_status: 'pending' };
            setSelectedOrder(updatedOrder);
            setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            
            toast.success('Document uploaded successfully!', { id: `upload-${docType}` });
        } catch (error) {
            console.error('Upload failed', error);
            toast.error(error.response?.data?.detail || 'Failed to upload document', { id: `upload-${docType}` });
        } finally {
            setUploadingDocs(prev => ({ ...prev, [docType]: false }));
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getStatusConfig = (status) => {
        const configs = {
            assigned: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle, label: 'New Order' },
            processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package, label: 'Processing' },
            shipped: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck, label: 'Shipped' },
            delivered: { color: 'bg-teal-100 text-teal-700 border-teal-200', icon: CheckCircle, label: 'Delivered' },
            completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Completed' }
        };
        return configs[status] || configs.assigned;
    };

    const getShortId = (id) => `#${(id || '').substring(0, 6).toUpperCase()}`;

    const filteredOrders = orders.filter(order => {
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        const matchesSearch = searchQuery === '' ||
            order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const stats = {
        newOrders: orders.filter(o => o.status === 'assigned').length,
        processing: orders.filter(o => o.status === 'processing').length,
        shipped: orders.filter(o => o.status === 'shipped').length,
        completed: orders.filter(o => ['delivered', 'completed'].includes(o.status)).length,
        revenue: orders.reduce((sum, o) => sum + (o.total_amount || 0), 0),
        avgRating: 4.8
    };

    const vehicleTypes = ['Tempo', 'Mini Truck', 'Pickup', 'Auto', 'Bike', 'Van', 'Other'];

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold">My Orders</h1>
                        <p className="text-muted-foreground mt-1">Manage and fulfill your assigned orders</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchOrders}>
                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
                        <p className="text-xs text-orange-700">New Orders</p>
                        <p className="text-2xl font-bold text-orange-700">{stats.newOrders}</p>
                        <p className="text-xs text-orange-600 mt-1">Needs attention</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                        <p className="text-xs text-blue-700">Processing</p>
                        <p className="text-2xl font-bold text-blue-700">{stats.processing}</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
                        <p className="text-xs text-purple-700">In Transit</p>
                        <p className="text-2xl font-bold text-purple-700">{stats.shipped}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                        <p className="text-xs text-green-700">Completed</p>
                        <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
                    </div>
                    <div className="bg-primary/10 rounded-xl border border-primary/20 p-4">
                        <p className="text-xs text-primary">Total Earnings</p>
                        <p className="text-2xl font-bold text-primary">₹{(stats.revenue / 100000).toFixed(1)}L</p>
                    </div>
                    <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
                        <p className="text-xs text-yellow-700">Rating</p>
                        <p className="text-2xl font-bold text-yellow-700 flex items-center gap-1">
                            {stats.avgRating} <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-card rounded-xl border p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by Order ID, Customer..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full md:w-[180px]">
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="assigned">New Orders</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Orders Grid */}
                <div className="grid gap-4">
                    {loading ? (
                        <div className="bg-card rounded-xl border p-12 text-center text-muted-foreground">Loading orders...</div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="bg-card rounded-xl border p-12 text-center">
                            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">No orders found</p>
                        </div>
                    ) : (
                        filteredOrders.map((order) => {
                            const statusConfig = getStatusConfig(order.status);
                            const StatusIcon = statusConfig.icon;
                            return (
                                <motion.div
                                    key={order.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-card rounded-xl border hover:shadow-md transition-shadow"
                                >
                                    {/* Order Header */}
                                    <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-3">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-xl ${statusConfig.color} flex items-center justify-center`}>
                                                <StatusIcon className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-mono font-bold text-lg">{getShortId(order.id)}</p>
                                                    <Badge className={`${statusConfig.color} border`}>{statusConfig.label}</Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    {order.assigned_at && ` • Assigned ${new Date(order.assigned_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-xl font-bold text-primary">₹{order.total_amount?.toLocaleString()}</p>
                                        </div>
                                    </div>

                                    {/* Order Content */}
                                    <div className="p-4 grid md:grid-cols-3 gap-4">
                                        {/* Customer & Shipping */}
                                        <div className="space-y-3">
                                            <div className="flex items-start gap-2">
                                                <User className="w-4 h-4 mt-1 text-muted-foreground" />
                                                <div>
                                                    <p className="font-medium">{order.customer?.name}</p>
                                                    <p className="text-xs text-muted-foreground">{order.customer?.email}</p>
                                                    <p className="text-xs text-muted-foreground">{order.customer?.phone}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <MapPin className="w-4 h-4 mt-1 text-muted-foreground" />
                                                <p className="text-sm">
                                                    {typeof order.shipping_address === 'object' && order.shipping_address
                                                        ? `${order.shipping_address.address}, ${order.shipping_address.city}, ${order.shipping_address.state} - ${order.shipping_address.pincode}`
                                                        : order.shipping_address}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Items */}
                                        <div>
                                            <p className="text-sm font-medium mb-2 flex items-center gap-1">
                                                <Package className="w-4 h-4" /> Items ({order.items?.length})
                                            </p>
                                            <div className="space-y-1">
                                                {order.items?.map((item, idx) => (
                                                    <div key={idx} className="text-sm bg-muted/30 rounded-lg p-2">
                                                        <p className="font-medium">{item.name}</p>
                                                        <p className="text-xs text-muted-foreground">Qty: {item.quantity} • ₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Tracking & Actions */}
                                        <div className="space-y-3">
                                            {order.shipping_info && (
                                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                                    <p className="text-xs text-purple-700 font-medium">Delivery Details</p>
                                                    <p className="text-sm font-medium">{order.shipping_info.delivery_person_name}</p>
                                                    <p className="text-xs text-muted-foreground">{order.shipping_info.vehicle_type} - {order.shipping_info.vehicle_number}</p>
                                                    {order.shipping_info.expected_delivery && (
                                                        <p className="text-xs text-green-600 mt-1">Expected: {new Date(order.shipping_info.expected_delivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                                                    )}
                                                </div>
                                            )}
                                            {order.notes && (
                                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                                                    <p className="text-xs text-yellow-700">{order.notes}</p>
                                                </div>
                                            )}
                                            {order.customer_rating && (
                                                <div className="flex items-center gap-1 text-sm">
                                                    <span>Customer Rating:</span>
                                                    {[...Array(order.customer_rating)].map((_, i) => (
                                                        <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Quick PoW Status */}
                                            {(order.pow_delivery_url || order.pow_bill_url || order.pow_agreement_url) && (
                                                <div className="flex flex-col gap-2 mt-2 pt-2 border-t">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-medium text-muted-foreground">Proof of Work Status:</span>
                                                        <Badge variant="outline" className={`text-[10px] uppercase ${
                                                            order.proof_of_work_status === 'approved' ? 'bg-green-100 text-green-700' :
                                                            order.proof_of_work_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                            'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {order.proof_of_work_status || 'PENDING'}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex gap-2 text-[10px]">
                                                        <span className={order.pow_delivery_url ? "text-green-600 font-medium flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"}>{order.pow_delivery_url ? <Check className="w-3 h-3"/> : <X className="w-3 h-3"/>} Delivery</span>
                                                        <span className={order.pow_bill_url ? "text-green-600 font-medium flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"}>{order.pow_bill_url ? <Check className="w-3 h-3"/> : <X className="w-3 h-3"/>} Bill</span>
                                                        <span className={order.pow_agreement_url ? "text-green-600 font-medium flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"}>{order.pow_agreement_url ? <Check className="w-3 h-3"/> : <X className="w-3 h-3"/>} Agreement</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Bar */}
                                    <div className="px-4 pb-4 flex flex-wrap gap-2">
                                        <Button variant="outline" size="sm" onClick={() => { setSelectedOrder(order); setShowDetailModal(true); }}>
                                            <Eye className="w-4 h-4 mr-1" /> View Details
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => { setSelectedOrder(order); handlePrint(); }}>
                                            <Printer className="w-4 h-4 mr-1" /> Packing Slip
                                        </Button>

                                        {order.status === 'assigned' && (
                                            <Button size="sm" onClick={() => updateOrderStatus(order.id, 'processing')}>
                                                <Package className="w-4 h-4 mr-1" /> Start Processing
                                            </Button>
                                        )}
                                        {order.status === 'provided' && null /* removed */}
                                        {order.status === 'processing' && (
                                            <Button size="sm" onClick={() => { setSelectedOrder(order); setShowShippingModal(true); }}>
                                                <Truck className="w-4 h-4 mr-1" /> Add Shipping
                                            </Button>
                                        )}
                                        {order.status === 'shipped' && (
                                            <Button size="sm" onClick={() => updateOrderStatus(order.id, 'delivered')}>
                                                <CheckCircle className="w-4 h-4 mr-1" /> Mark Delivered
                                            </Button>
                                        )}
                                        {order.status === 'delivered' && (
                                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateOrderStatus(order.id, 'completed')}>
                                                <Check className="w-4 h-4 mr-1" /> Complete Order
                                            </Button>
                                        )}
                                        
                                        {/* Quick Upload Proof Button */}
                                        {(order.status === 'delivered' || order.status === 'completed') && (!order.pow_delivery_url || !order.pow_bill_url || !order.pow_agreement_url || order.proof_of_work_status === 'rejected') && (
                                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100" onClick={() => { setSelectedOrder(order); setShowDetailModal(true); }}>
                                                <ImageIcon className="w-4 h-4 mr-1" /> Upload Proof Docs
                                            </Button>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })
                    )}
                </div>

                {/* Order Detail Modal */}
                <AnimatePresence>
                    {showDetailModal && selectedOrder && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => setShowDetailModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-5xl my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold font-mono">{getShortId(selectedOrder.id)}</h2>
                                            <Badge className={`${getStatusConfig(selectedOrder.status).color} border`}>
                                                {getStatusConfig(selectedOrder.status).label}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Assigned {new Date(selectedOrder.assigned_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setShowDetailModal(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>

                                <div className="p-6 grid md:grid-cols-2 gap-6">
                                    {/* Left Column */}
                                    <div className="space-y-4">
                                        {/* Customer */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <User className="w-4 h-4" /> Customer Details
                                            </h3>
                                            <p className="font-medium">{selectedOrder.customer?.name}</p>
                                            <div className="mt-2 space-y-1 text-sm">
                                                <p className="flex items-center gap-2 text-muted-foreground">
                                                    <Mail className="w-3 h-3" /> {selectedOrder.customer?.email}
                                                </p>
                                                <a href={`tel:${selectedOrder.customer?.phone}`} className="flex items-center gap-2 text-primary hover:underline">
                                                    <Phone className="w-3 h-3" /> {selectedOrder.customer?.phone}
                                                </a>
                                            </div>
                                        </div>

                                        {/* Shipping */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <MapPin className="w-4 h-4" /> Delivery Address
                                            </h3>
                                            <p className="text-sm">
                                                {typeof selectedOrder.shipping_address === 'object' && selectedOrder.shipping_address
                                                    ? `${selectedOrder.shipping_address.address}, ${selectedOrder.shipping_address.city}, ${selectedOrder.shipping_address.state} - ${selectedOrder.shipping_address.pincode}`
                                                    : selectedOrder.shipping_address}
                                            </p>
                                            <Button variant="link" size="sm" className="p-0 h-auto mt-2" asChild>
                                                <a href={`https://maps.google.com/?q=${encodeURIComponent(
                                                    typeof selectedOrder.shipping_address === 'object' && selectedOrder.shipping_address
                                                        ? `${selectedOrder.shipping_address.address}, ${selectedOrder.shipping_address.city}, ${selectedOrder.shipping_address.state}`
                                                        : selectedOrder.shipping_address
                                                )}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="w-3 h-3 mr-1" /> Open in Maps
                                                </a>
                                            </Button>
                                        </div>

                                        {/* Items */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <Package className="w-4 h-4" /> Order Items
                                            </h3>
                                            <div className="space-y-2">
                                                {selectedOrder.items?.map((item, idx) => (
                                                    <div key={idx} className="flex justify-between items-center p-2 bg-card rounded-lg">
                                                        <div>
                                                            <p className="font-medium text-sm">{item.name}</p>
                                                            <p className="text-xs text-muted-foreground">SKU: {item.sku || 'N/A'} • Qty: {item.quantity}</p>
                                                        </div>
                                                        <p className="font-semibold">₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            <hr className="my-3" />
                                            <div className="flex justify-between font-bold">
                                                <span>Total</span>
                                                <span className="text-primary">₹{selectedOrder.total_amount?.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Timeline */}
                                    <div className="space-y-4">
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <Calendar className="w-4 h-4" /> Order Timeline
                                            </h3>
                                            <div className="space-y-4">
                                                {(selectedOrder.timeline || []).map((event, idx) => {
                                                    const config = getStatusConfig(event.status);
                                                    const EventIcon = config.icon;
                                                    return (
                                                        <div key={idx} className="flex gap-3">
                                                            <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center flex-shrink-0`}>
                                                                <EventIcon className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="font-medium text-sm capitalize">{event.status}</p>
                                                                <p className="text-xs text-muted-foreground">{event.note}</p>
                                                                <p className="text-xs text-muted-foreground">{new Date(event.timestamp).toLocaleString()}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Shipping Info */}
                                        {selectedOrder.shipping_info && (
                                            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                                                <h3 className="font-semibold flex items-center gap-2 mb-3 text-purple-700">
                                                    <Truck className="w-4 h-4" /> Delivery Details
                                                </h3>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Delivery Person</span>
                                                        <span className="font-medium">{selectedOrder.shipping_info.delivery_person_name}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Phone</span>
                                                        <a href={`tel:${selectedOrder.shipping_info.delivery_person_phone}`} className="text-primary font-medium hover:underline">
                                                            {selectedOrder.shipping_info.delivery_person_phone}
                                                        </a>
                                                    </div>
                                                    {selectedOrder.shipping_info.vehicle_type && (
                                                        <div className="flex justify-between">
                                                            <span className="text-muted-foreground">Vehicle</span>
                                                            <span className="font-medium">{selectedOrder.shipping_info.vehicle_type} - {selectedOrder.shipping_info.vehicle_number}</span>
                                                        </div>
                                                    )}
                                                    {!selectedOrder.shipping_info.vehicle_type && selectedOrder.shipping_info.vehicle_number && (
                                                        <div className="flex justify-between">
                                                            <span className="text-muted-foreground">Vehicle No.</span>
                                                            <span className="font-medium">{selectedOrder.shipping_info.vehicle_number}</span>
                                                        </div>
                                                    )}
                                                    {selectedOrder.shipping_info.expected_delivery && (
                                                        <div className="flex justify-between">
                                                            <span className="text-muted-foreground">Expected Delivery</span>
                                                            <span className="font-medium text-green-600">{new Date(selectedOrder.shipping_info.expected_delivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                                        </div>
                                                    )}
                                                    {selectedOrder.shipping_info.delivery_notes && (
                                                        <div className="mt-2 p-2 bg-white rounded-lg">
                                                            <p className="text-xs text-muted-foreground">Notes: {selectedOrder.shipping_info.delivery_notes}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold mb-3">Actions</h3>
                                            <div className="space-y-2">
                                                {selectedOrder.status === 'assigned' && (
                                                    <Button className="w-full" onClick={() => { updateOrderStatus(selectedOrder.id, 'processing'); setShowDetailModal(false); }}>
                                                        <Package className="w-4 h-4 mr-2" /> Start Processing
                                                    </Button>
                                                )}
                                                {selectedOrder.status === 'processing' && (
                                                    <Button className="w-full" onClick={() => { setShowDetailModal(false); setShowShippingModal(true); }}>
                                                        <Truck className="w-4 h-4 mr-2" /> Add Shipping Details
                                                    </Button>
                                                )}
                                                {selectedOrder.status === 'shipped' && (
                                                    <Button className="w-full" onClick={() => { updateOrderStatus(selectedOrder.id, 'delivered'); setShowDetailModal(false); }}>
                                                        <CheckCircle className="w-4 h-4 mr-2" /> Mark as Delivered
                                                    </Button>
                                                )}
                                                {selectedOrder.status === 'delivered' && (
                                                    <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => { updateOrderStatus(selectedOrder.id, 'completed'); setShowDetailModal(false); }}>
                                                        <Check className="w-4 h-4 mr-2" /> Complete Order
                                                    </Button>
                                                )}
                                                <Button variant="outline" className="w-full" onClick={() => handlePrint()}>
                                                    <Printer className="w-4 h-4 mr-2" /> Print Packing Slip
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Proof of Work Multi-Upload Section */}
                                    <div className="bg-muted/20 border-t p-6 md:col-span-2">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-bold flex items-center gap-2">
                                                <ImageIcon className="w-5 h-5 text-primary" /> Required Proof Documents
                                            </h3>
                                            {selectedOrder.proof_of_work_status && (
                                                <Badge variant="outline" className={`px-3 py-1 text-xs uppercase ${
                                                    selectedOrder.proof_of_work_status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                    selectedOrder.proof_of_work_status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                    'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                }`}>
                                                    Status: {selectedOrder.proof_of_work_status}
                                                </Badge>
                                            )}
                                        </div>
                                        
                                        {selectedOrder.proof_of_work_notes && selectedOrder.proof_of_work_status === 'rejected' && (
                                            <div className="p-4 mb-6 bg-red-50 text-red-700 rounded-xl border border-red-200 flex gap-3">
                                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                                <div>
                                                    <strong className="block mb-1">Admin Rejection Note:</strong>
                                                    <p className="text-sm">{selectedOrder.proof_of_work_notes}</p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid md:grid-cols-3 gap-6">
                                            {/* Column 1: Proof of Delivery */}
                                            <div className="bg-card rounded-xl border overflow-hidden p-4 space-y-3">
                                                <h4 className="font-semibold text-sm text-center">1. Proof of Delivery</h4>
                                                {selectedOrder.pow_delivery_url ? (
                                                    <div className="space-y-3">
                                                        <div className="aspect-square bg-muted rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_delivery_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_delivery_url}` : selectedOrder.pow_delivery_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_delivery_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_delivery_url}` : selectedOrder.pow_delivery_url} alt="Proof of Delivery" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                        {selectedOrder.proof_of_work_status !== 'approved' && (
                                                            <label className="flex items-center justify-center w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 h-9 px-4 py-2 rounded-md cursor-pointer text-xs font-medium transition-colors">
                                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'delivery')} disabled={uploadingDocs.delivery} />
                                                                {uploadingDocs.delivery ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                                                {uploadingDocs.delivery ? 'Uploading...' : 'Replace'}
                                                            </label>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors">
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'delivery')} disabled={uploadingDocs.delivery} />
                                                        {uploadingDocs.delivery ? <RefreshCw className="w-8 h-8 mb-2 text-muted-foreground animate-spin" /> : <Upload className="w-8 h-8 mb-2 text-muted-foreground opacity-50" />}
                                                        <span className="text-xs font-medium text-muted-foreground text-center px-4">{uploadingDocs.delivery ? 'Uploading...' : 'Upload Delivery Proof'}</span>
                                                    </label>
                                                )}
                                            </div>

                                            {/* Column 2: Customer Bill */}
                                            <div className="bg-card rounded-xl border overflow-hidden p-4 space-y-3">
                                                <h4 className="font-semibold text-sm text-center">2. Customer Bill</h4>
                                                {selectedOrder.pow_bill_url ? (
                                                    <div className="space-y-3">
                                                        <div className="aspect-square bg-muted rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_bill_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_bill_url}` : selectedOrder.pow_bill_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_bill_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_bill_url}` : selectedOrder.pow_bill_url} alt="Customer Bill" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                        {selectedOrder.proof_of_work_status !== 'approved' && (
                                                            <label className="flex items-center justify-center w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 h-9 px-4 py-2 rounded-md cursor-pointer text-xs font-medium transition-colors">
                                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'bill')} disabled={uploadingDocs.bill} />
                                                                {uploadingDocs.bill ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                                                {uploadingDocs.bill ? 'Uploading...' : 'Replace'}
                                                            </label>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors">
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'bill')} disabled={uploadingDocs.bill} />
                                                        {uploadingDocs.bill ? <RefreshCw className="w-8 h-8 mb-2 text-muted-foreground animate-spin" /> : <Upload className="w-8 h-8 mb-2 text-muted-foreground opacity-50" />}
                                                        <span className="text-xs font-medium text-muted-foreground text-center px-4">{uploadingDocs.bill ? 'Uploading...' : 'Upload Bill'}</span>
                                                    </label>
                                                )}
                                            </div>

                                            {/* Column 3: Customer Agreement */}
                                            <div className="bg-card rounded-xl border overflow-hidden p-4 space-y-3">
                                                <h4 className="font-semibold text-sm text-center">3. Customer Agreement</h4>
                                                {selectedOrder.pow_agreement_url ? (
                                                    <div className="space-y-3">
                                                        <div className="aspect-square bg-muted rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_agreement_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_agreement_url}` : selectedOrder.pow_agreement_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_agreement_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_agreement_url}` : selectedOrder.pow_agreement_url} alt="Customer Agreement" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                        {selectedOrder.proof_of_work_status !== 'approved' && (
                                                            <label className="flex items-center justify-center w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 h-9 px-4 py-2 rounded-md cursor-pointer text-xs font-medium transition-colors">
                                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'agreement')} disabled={uploadingDocs.agreement} />
                                                                {uploadingDocs.agreement ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                                                {uploadingDocs.agreement ? 'Uploading...' : 'Replace'}
                                                            </label>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors">
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDocUpload(e, 'agreement')} disabled={uploadingDocs.agreement} />
                                                        {uploadingDocs.agreement ? <RefreshCw className="w-8 h-8 mb-2 text-muted-foreground animate-spin" /> : <Upload className="w-8 h-8 mb-2 text-muted-foreground opacity-50" />}
                                                        <span className="text-xs font-medium text-muted-foreground text-center px-4">{uploadingDocs.agreement ? 'Uploading...' : 'Upload Agreement'}</span>
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Shipping Modal */}
                <AnimatePresence>
                    {showShippingModal && selectedOrder && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => setShowShippingModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-md my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b">
                                    <h2 className="text-xl font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-primary" /> Add Shipping Details</h2>
                                    <p className="text-sm text-muted-foreground mt-1">Order {getShortId(selectedOrder.id)} • Your delivery team details</p>
                                </div>
                                <div className="p-6 space-y-4">
                                    {/* Delivery Person Section */}
                                    <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                                        <p className="text-sm font-semibold text-blue-700 flex items-center gap-1"><User className="w-4 h-4" /> Delivery Person</p>
                                        <div className="space-y-2">
                                            <Label>Name *</Label>
                                            <Input
                                                placeholder="e.g. Ramesh Kumar"
                                                value={shippingForm.delivery_person_name}
                                                onChange={e => setShippingForm({ ...shippingForm, delivery_person_name: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone Number *</Label>
                                            <Input
                                                placeholder="e.g. 9876543210"
                                                value={shippingForm.delivery_person_phone}
                                                onChange={e => setShippingForm({ ...shippingForm, delivery_person_phone: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {/* Vehicle Section */}
                                    <div className="bg-purple-50 rounded-xl p-4 space-y-3">
                                        <p className="text-sm font-semibold text-purple-700 flex items-center gap-1"><Truck className="w-4 h-4" /> Vehicle Details</p>
                                        <div className="space-y-2">
                                            <Label>Vehicle Type</Label>
                                            <Select value={shippingForm.vehicle_type} onValueChange={v => setShippingForm({ ...shippingForm, vehicle_type: v })}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select vehicle type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {vehicleTypes.map(v => (
                                                        <SelectItem key={v} value={v}>{v}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Vehicle Number *</Label>
                                            <Input
                                                placeholder="e.g. CH-01-AB-1234"
                                                value={shippingForm.vehicle_number}
                                                onChange={e => setShippingForm({ ...shippingForm, vehicle_number: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {/* Delivery Info */}
                                    <div className="space-y-3">
                                        <div className="space-y-2">
                                            <Label>Expected Delivery Date *</Label>
                                            <Input
                                                type="date"
                                                value={shippingForm.expected_delivery}
                                                onChange={e => setShippingForm({ ...shippingForm, expected_delivery: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Delivery Notes</Label>
                                            <Textarea
                                                placeholder="Any special instructions for delivery..."
                                                value={shippingForm.delivery_notes}
                                                onChange={e => setShippingForm({ ...shippingForm, delivery_notes: e.target.value })}
                                                rows={2}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-2 pt-2">
                                        <Button className="flex-1" onClick={handleShipOrder}>
                                            <Truck className="w-4 h-4 mr-2" /> Mark as Shipped
                                        </Button>
                                        <Button variant="outline" onClick={() => setShowShippingModal(false)}>Cancel</Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Hidden Invoice for Printing */}
                <div className="hidden">
                    <OrderInvoice ref={printRef} order={selectedOrder} isVendor={true} vendorName={user?.name} />
                </div>
            </div>
        </DashboardLayout>
    );
};

export default VendorOrders;
